/* eslint-disable react/no-unused-prop-types */
import React, { useEffect } from 'react';
import type { DraggableSyntheticListeners } from '@dnd-kit/core';
import type { Transform } from '@dnd-kit/utilities';
import classNames from 'classnames';
import { Handle } from './components';
import styles from './Item.module.scss';
import { useInputWidth } from '../../../../../../shared/ui/editable-text/hooks/useInputWidth';
import { useFocusOnCondition } from '../../../../../../hooks/useFocusOnCondition';
import { ITag } from '../../../../../../entities/module/private-template-module-tag/api/types';
import css from '../../../../../../entities/module/private-template-module-tag/ui/editable-dnd-tag/editable-dnd-tag.module.scss';
import { ModuleTagControls } from '../../../../../../entities/module/private-template-module-tag/ui/controls/controls';

export interface Props {
	dragOverlay?: boolean;
	disabled?: boolean;
	dragging?: boolean;
	handle?: boolean;
	handleProps?: any;
	height?: number;
	index?: number;
	fadeIn?: boolean;
	transform?: Transform | null;
	listeners?: DraggableSyntheticListeners;
	sorting?: boolean;
	style?: React.CSSProperties;
	transition?: string | null;
	wrapperStyle?: React.CSSProperties;
	tag: ITag;
	isActive?: boolean,
	className?: string,
	onClick?: (id: number) => void,
	onDelete?: (e: React.MouseEvent) => void;
	updateTagName: () => void,
	editableTag: number | null;
	handleTagNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
	handleInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void,
	handleNameKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void,
	newTagName: string,
	handleNameClick: (e: React.MouseEvent) => void,
	draggable?: boolean,
	underDragging?: boolean,
	paddingRight?: number,
	groupId: string,
	onRename?():void;
	renderItem?(args: {
		dragOverlay: boolean;
		dragging: boolean;
		sorting: boolean;
		index: number | undefined;
		fadeIn: boolean;
		listeners: DraggableSyntheticListeners;
		ref: React.Ref<HTMLLIElement>;
		style: React.CSSProperties | undefined;
		transform: Props['transform'];
		transition: Props['transition'];
	}): React.ReactElement;
}

export const Item = React.memo(
	React.forwardRef<HTMLLIElement, Props>(
		(
			{
				tag,
				onClick,
				isActive,
				onDelete,
				className,
				updateTagName,
				editableTag,
				handleTagNameChange,
				handleInputKeyDown,
				handleNameKeyDown,
				newTagName,
				handleNameClick,
				draggable,
				dragging,
				underDragging,
				paddingRight,
				groupId,
				onRename,
				dragOverlay,
				disabled,
				fadeIn,
				handle,
				handleProps,
				height,
				index,
				listeners,
				renderItem,
				sorting,
				style,
				transition,
				transform,
				wrapperStyle,
				...props
			},
			ref,
		) => {
			useEffect(() => {
				if (!dragOverlay) {
					return;
				}

				document.body.style.cursor = 'grabbing';

				return () => {
					document.body.style.cursor = '';
				};
			}, [dragOverlay]);

			const { id, name } = tag;
			const { inputRef, sizerRef, inputWidth } = useInputWidth(newTagName, editableTag, id, paddingRight);

			useFocusOnCondition(inputRef, editableTag === id, {
				setCursorToEnd: true,
			});

			const handleTagClick = (e: React.MouseEvent) => {
				handleNameClick(e);
			};

			return (
				<li
					ref={ref}
					className={classNames(
						styles.Wrapper,
						fadeIn && styles.fadeIn,
						sorting && styles.sorting,
						dragOverlay && styles.dragOverlay,
					)}
					style={{
						...wrapperStyle,
						transition: [transition, wrapperStyle?.transition].filter(Boolean).join(', '),
						'--translate-x': transform ? `${Math.round(transform.x)}px` : undefined,
						'--translate-y': transform ? `${Math.round(transform.y)}px` : undefined,
						'--index': index,
					} as React.CSSProperties}
				>
					<div
						className={classNames(
							styles.Item,
							dragging && styles.dragging,
							handle && styles.withHandle,
							dragOverlay && styles.dragOverlay,
							disabled && styles.disabled,
						)}
						style={style}
						data-cypress="draggable-item"
						{...(!handle ? listeners : undefined)}
						{...props}
						tabIndex={!handle ? 0 : undefined}
					>
						{draggable && (
							<div>
								{handle ? <Handle {...handleProps} {...listeners} /> : null}
							</div>
						)}
						{editableTag === id ? (
							<>
								<input
									ref={inputRef}
									type="text"
									value={newTagName}
									onChange={handleTagNameChange}
									onBlur={(e) => {
										e.stopPropagation();
										updateTagName();
									}}
									onKeyDown={handleInputKeyDown}
									onClick={(e) => e.stopPropagation()}
									onFocus={(e) => e.stopPropagation()}
									className={css.titleActive}
									style={{
										width: inputWidth ? `${inputWidth}px` : 'auto',
										visibility: editableTag === id ? 'visible' : 'hidden',
									}}
								/>
								<span ref={sizerRef} className={css.sizer}>
									{newTagName || ' '}
								</span>
							</>
						) : (
							<div
								role="button"
								tabIndex={0}
								onKeyDown={handleNameKeyDown}
								className={css.text}
								onClick={handleTagClick}
							>
								{name}
							</div>
						)}
						<ModuleTagControls
							isIconShow={false}
							onDelete={(e) => onDelete && onDelete(e)}
						/>
					</div>
				</li>
			);
		},
	),
);
